import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '@lib/routes';

const generateOrderRef = (): string => {
  const year = new Date().getFullYear();
  const num = Math.floor(10000 + Math.random() * 90000);
  return `ORD-${year}-${num}`;
};

export default function Success() {
  const navigate = useNavigate();
  const [orderRef, setOrderRef] = useState<string | null>(null);

  useEffect(() => {
    const existing = localStorage.getItem('checkio_reference_id');
    if (existing) {
      setOrderRef(existing);
    } else {
      const ref = generateOrderRef();
      setOrderRef(ref);
      localStorage.setItem('checkio_reference_id', ref);
    }
  }, []);

  if (!orderRef) return null;

  return (
    <div className="flex min-h-screen w-full flex-col bg-veritas-gray-50 font-sans">
      {/* Top bar */}
      <div className="h-12 bg-veritas-navy flex items-center justify-between px-4 sm:px-6 shrink-0">
        <a href="/" className="flex items-center justify-center shrink-0" aria-label="Home">
          <img src="/brand/veritas-inverse.svg" alt="Veritas" className="h-8 w-auto" />
        </a>
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center px-6 pt-16 pb-12">
        <div className="flex max-w-xl w-full flex-col items-center gap-8">

          {/* Check icon */}
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-veritas-navy shrink-0">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          {/* Heading + order ref */}
          <div className="flex flex-col items-center gap-3 text-center">
            <h1 className="font-sans text-4xl font-bold text-veritas-navy">
              Thank you for your payment
            </h1>
            <div className="flex items-center gap-2">
              <span className="text-sm text-veritas-gray-500">Order reference:</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md border border-veritas-gray-200 bg-white font-mono text-sm text-veritas-navy shadow-sm">
                {orderRef}
              </span>
            </div>
          </div>

          {/* Status card */}
          <div className="flex w-full flex-col items-center gap-4 rounded-xl bg-white px-8 py-8 shadow-md border border-veritas-gray-200 text-center">
            <span className="font-sans font-semibold text-veritas-navy">
              We are currently processing your file
            </span>
            <span className="text-sm text-veritas-gray-500 leading-relaxed max-w-sm">
              A legal forensic advisor will get in touch with you if we need more details.
            </span>
            <span className="text-xs text-veritas-gray-400">
              You will receive a response within 72 hours
            </span>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 w-full justify-center pt-2">
            <button
              onClick={() => {
                ['checkio_file', 'checkio_hash', 'checkio_contact', 'checkio_tier',
                 'checkio_payment', 'checkio_reference_id', 'checkio_exif', 'checkio_billing'].forEach(k => localStorage.removeItem(k));
                navigate('/');
              }}
              className="inline-flex items-center justify-center px-6 py-3 bg-veritas-navy text-white font-sans font-semibold text-sm rounded-xl hover:bg-veritas-navy/90 transition-colors"
            >
              Back to Home
            </button>
            <button
              onClick={() => navigate(routes.app)}
              className="inline-flex items-center justify-center px-6 py-3 border border-veritas-gray-200 bg-white text-veritas-navy font-sans font-medium text-sm rounded-xl hover:bg-veritas-gray-50 transition-colors"
            >
              Verify Another File
            </button>
          </div>

        </div>
      </main>

      {/* Footer — matches landing page */}
      <footer className="bg-veritas-navy px-4 sm:px-6 lg:px-8 py-6 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 p-0 m-0 leading-none pointer-events-none">
          <img src="/brand/veritas-inverse.svg" alt="" aria-hidden="true" className="h-24 md:h-28 lg:h-32 w-auto opacity-95" />
        </div>
        <div
          className="absolute top-0 right-0 w-64 h-64 md:w-96 md:h-96 opacity-10"
          style={{ background: 'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.15) 0%, transparent 60%)' }}
        />
        <div className="max-w-6xl mx-auto relative">
          <div className="flex flex-col md:flex-row md:justify-between gap-8">
            <p className="text-white/60 text-sm">© 2026 Veritas.</p>
            <nav className="grid grid-cols-2 sm:grid-cols-4 gap-8">
              {[
                { title: 'PRODUCT', links: ['Upload', 'How It Works', 'Pricing'] },
                { title: 'COMPANY', links: ['About', 'Contact'] },
                { title: 'RESOURCES', links: ['FAQ', 'Formats'] },
                { title: 'LEGAL', links: ['Privacy', 'Terms'] },
              ].map((section) => (
                <div key={section.title}>
                  <p className="text-white font-sans font-semibold text-xs mb-3 tracking-wider">{section.title}</p>
                  <ul className="space-y-2 text-white/60 text-sm">
                    {section.links.map((link) => (
                      <li key={link}>{link}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
